import { useState, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  Tag,
  Package,
  Phone,
  ShieldCheck,
  Truck,
  Leaf,
  Star,
  ClipboardList,
  CheckCircle2,
  Coins,
  MapPin,
  Clock,
  Home,
  Send,
  Loader2,
  MessageSquareHeart,
  AlertCircle,
  ShoppingBag,
} from 'lucide-react';
import clsx from 'clsx';
import { useProductBySlug, useSimilarProducts } from '../services/productService';
import { useProductReviews, useSubmitReview, MAX_REVIEW_LENGTH } from '../services/reviewService';
import { getImageUrl } from '../lib/supabase';
import { formatPrice } from '../lib/utils';
import { useSiteSettings } from '../services/settingsService';
import { ImageWithPlaceholder } from '../components/ImageWithPlaceholder/ImageWithPlaceholder';
import { StarRating } from '../components/StarRating/StarRating';
import { OrderModal } from '../components/OrderModal/OrderModal';

/* ── Image Carousel ── */
function ImageCarousel({ images, title, isHero = false }) {
  const [current, setCurrent] = useState(0);

  const prev = () => setCurrent((c) => (c === 0 ? images.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === images.length - 1 ? 0 : c + 1));

  // Hero mode: absolute-fill background images with no container chrome
  if (isHero) {
    return (
      <>
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={images[current]}
            alt={`${title} - ${current + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 w-full h-full object-cover z-[1]"
          />
        </AnimatePresence>

        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-brand-white/80 shadow-md hover:bg-brand-white transition-colors z-20"
              aria-label="Previous image"
            >
              <ChevronLeft size={20} className="text-brand-dark" />
            </button>
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-brand-white/80 shadow-md hover:bg-brand-white transition-colors z-20"
              aria-label="Next image"
            >
              <ChevronRight size={20} className="text-brand-dark" />
            </button>
          </>
        )}

        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={clsx(
                  'rounded-full transition-all duration-300',
                  i === current
                    ? 'w-6 h-2 bg-brand-white'
                    : 'w-2 h-2 bg-brand-white/50 hover:bg-brand-white/75'
                )}
                aria-label={`Go to image ${i + 1}`}
              />
            ))}
          </div>
        )}
      </>
    );
  }

  // Default card mode
  return (
    <div className="relative rounded-2xl overflow-hidden bg-brand-dark">
      <AnimatePresence mode="wait">
        <motion.div
          key={current}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ImageWithPlaceholder
            src={images[current]}
            alt={`${title} - ${current + 1}`}
            aspectRatio="aspect-[4/3]"
            className="w-full"
          />
        </motion.div>
      </AnimatePresence>

      {images.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-brand-white/90 shadow-md hover:bg-brand-white transition-colors z-10"
            aria-label="Previous image"
          >
            <ChevronLeft size={20} className="text-brand-dark" />
          </button>
          <button
            onClick={next}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-brand-white/90 shadow-md hover:bg-brand-white transition-colors z-10"
            aria-label="Next image"
          >
            <ChevronRight size={20} className="text-brand-dark" />
          </button>
        </>
      )}

      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={clsx(
                'rounded-full transition-all duration-300',
                i === current
                  ? 'w-6 h-2 bg-brand-white'
                  : 'w-2 h-2 bg-brand-white/50 hover:bg-brand-white/75'
              )}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </div>
      )}

      {images.length > 1 && (
        <span className="absolute bottom-3 right-3 px-2.5 py-1 text-xs font-medium bg-brand-dark/60 text-brand-white rounded-md z-10">
          {current + 1} / {images.length}
        </span>
      )}
    </div>
  );
}

/* ── Similar Options slider ── */
function SimilarSlider({ items }) {
  const scrollRef = useRef(null);

  const scroll = useCallback((dir) => {
    if (!scrollRef.current) return;
    const amount = 220;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.3 }}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-brand-dark">Similar Options</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => scroll('left')}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-ui-border bg-brand-white hover:bg-brand-light transition-colors"
            aria-label="Scroll left"
          >
            <ChevronLeft size={16} className="text-brand-dark" />
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-8 h-8 flex items-center justify-center rounded-full border border-ui-border bg-brand-white hover:bg-brand-light transition-colors"
            aria-label="Scroll right"
          >
            <ChevronRight size={16} className="text-brand-dark" />
          </button>
        </div>
      </div>
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
      >
        {items.map((item) => (
          <Link
            key={item.id}
            to={`/menu/${item.slug}`}
            className="snap-start flex-shrink-0 w-44 sm:w-52 bg-brand-white rounded-xl border border-ui-border overflow-hidden hover:shadow-md transition-shadow no-underline group"
          >
            <div className="overflow-hidden">
              <ImageWithPlaceholder
                src={getImageUrl(item.image_path)}
                alt={item.title}
                aspectRatio="aspect-[4/3]"
                className="w-full group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="p-3">
              <p className="text-sm font-bold text-brand-dark truncate">{item.title}</p>
              <p className="text-xs text-brand-gray mt-0.5 truncate">{item.short_description}</p>
              <p className="text-sm font-extrabold text-brand-red mt-2">{formatPrice(item.price)}</p>
            </div>
          </Link>
        ))}
      </div>
    </motion.div>
  );
}

/**
 * Product Detail page — two-column layout.
 * Left: image carousel + specs + description + steps + similar.
 * Right: sticky price/CTA sidebar card.
 * Mobile: stacked with sticky bottom CTA.
 */
export default function ProductDetailPage() {
  const { slug } = useParams();
  const { data: product, isLoading, error } = useProductBySlug(slug);
  const { data: settings } = useSiteSettings();
  const [orderOpen, setOrderOpen] = useState(false);

  const { data: similarProducts } = useSimilarProducts(
    product?.category_id,
    product?.id
  );

  // -- Loading skeleton --
  if (isLoading) {
    return (
      <main className="min-h-screen bg-ui-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4 animate-pulse">
              <div className="w-full aspect-[4/3] bg-brand-light rounded-2xl" />
              <div className="flex gap-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="w-24 h-16 bg-brand-light rounded-lg" />
                ))}
              </div>
              <div className="h-6 bg-brand-light rounded w-1/2" />
              <div className="h-4 bg-brand-light rounded w-full" />
              <div className="h-4 bg-brand-light rounded w-5/6" />
            </div>
            <div className="space-y-4 animate-pulse">
              <div className="h-64 bg-brand-light rounded-2xl" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  // -- Error state --
  if (error || !product) {
    return (
      <main className="min-h-screen bg-ui-background flex items-center justify-center">
        <div className="text-center px-4">
          <h2 className="text-2xl font-bold text-brand-dark mb-2">Product Not Found</h2>
          <p className="text-brand-gray mb-6">The product you're looking for doesn't exist.</p>
          <Link
            to="/menu"
            className="inline-flex items-center gap-2 px-6 py-3 bg-brand-red text-brand-white font-bold rounded-xl hover:bg-red-700 transition-colors"
          >
            <ChevronLeft size={18} />
            Back to Menu
          </Link>
        </div>
      </main>
    );
  }

  // Build images array: primary + extras (up to 3 total)
  const primaryImage = getImageUrl(product.image_path);
  const extraImages = (product.images || []).filter(Boolean).map(getImageUrl);
  const allImages = [primaryImage, ...extraImages].slice(0, 3);

  const categoryName = product.categories?.name;

  return (
    <>
      <main className="min-h-screen bg-ui-background pb-24 lg:pb-8">

        {/* ── Hero area — product image as full-width background with breadcrumbs ── */}
        <section className="relative min-h-[32vh] sm:min-h-[38vh] lg:min-h-[44vh] flex items-end overflow-hidden">
          {/* Background — uses carousel for multiple images */}
          <div className="absolute inset-0">
            <ImageCarousel images={allImages} title={product.title} isHero />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/80 via-brand-dark/30 to-brand-dark/10 z-[5]" />
          </div>

          {/* Badges — icon + text */}
          {product.is_featured && (
            <span className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 bg-brand-red text-brand-white text-xs font-bold rounded-lg shadow-md z-20">
              <Star size={14} className="fill-brand-white" />
              Featured
            </span>
          )}

          {/* Content overlay — breadcrumbs + title */}
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-5 sm:pb-6">
            {/* Breadcrumbs */}
            <nav className="flex items-center gap-1.5 text-sm mb-3">
              <Link
                to="/"
                className="flex items-center gap-1 text-brand-white/70 hover:text-brand-white transition-colors"
              >
                <Home size={14} />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <ChevronRight size={14} className="text-brand-white/40" />
              <Link
                to="/menu"
                className="text-brand-white/70 hover:text-brand-white transition-colors"
              >
                Menu
              </Link>
              <ChevronRight size={14} className="text-brand-white/40" />
              <span className="text-brand-white font-medium truncate max-w-[200px] sm:max-w-none">
                {product.title}
              </span>
            </nav>

            {/* Title + rating */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-brand-white drop-shadow-lg leading-tight">
                {product.title}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <StarRating rating={5} size={16} />
                {categoryName && (
                  <span className="flex items-center gap-1 text-sm text-brand-white/70">
                    <Tag size={12} />
                    {categoryName}
                  </span>
                )}
                <span className="text-xl sm:text-2xl font-extrabold text-brand-white drop-shadow">
                  {formatPrice(product.price)}
                </span>
              </div>
            </motion.div>

            {/* Thumbnail strip */}
            {allImages.length > 1 && (
              <div className="flex gap-2 mt-3">
                {allImages.map((src, i) => (
                  <button
                    key={i}
                    className="w-16 h-12 sm:w-20 sm:h-14 rounded-lg overflow-hidden border-2 border-brand-white/30 hover:border-brand-white transition-all flex-shrink-0"
                  >
                    <img
                      src={src}
                      alt={`View ${i + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── Two-column layout ── */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ══ LEFT COLUMN ══ */}
            <div className="lg:col-span-2 space-y-5">

              {/* ── Trust badges row — icon-focused ── */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 }}
                className="flex items-center gap-2 sm:gap-3 flex-wrap"
              >
                {[
                  { icon: ShieldCheck, label: 'Quality Verified', color: 'text-success', bg: 'bg-green-50' },
                  { icon: Leaf, label: 'Fresh Ingredients', color: 'text-success', bg: 'bg-green-50' },
                  { icon: Truck, label: 'Delivery Available', color: 'text-brand-red', bg: 'bg-red-50' },
                  { icon: ShoppingBag, label: 'Easy Ordering', color: 'text-success', bg: 'bg-green-50' },
                ].map(({ icon: Icon, label, color, bg }) => (
                  <div
                    key={label}
                    title={label}
                    className={clsx('flex items-center gap-2 px-3 py-2 rounded-xl border border-ui-border', bg)}
                  >
                    <Icon size={18} className={clsx(color, 'flex-shrink-0')} />
                    <span className="text-xs font-semibold text-brand-dark hidden sm:inline">{label}</span>
                  </div>
                ))}
              </motion.div>

              {/* ── Quick specs grid ── */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15 }}
                className="bg-brand-white rounded-2xl border border-ui-border p-4 sm:p-5"
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                  <div className="flex flex-col items-center text-center p-3 rounded-xl bg-brand-light/50">
                    <Tag size={20} className="text-brand-red mb-1.5" />
                    <span className="text-xs text-brand-gray">Category</span>
                    <span className="text-sm font-bold text-brand-dark mt-0.5">{categoryName || 'General'}</span>
                  </div>
                  <div className="flex flex-col items-center text-center p-3 rounded-xl bg-brand-light/50">
                    <Coins size={20} className="text-warning mb-1.5" />
                    <span className="text-xs text-brand-gray">Price</span>
                    <span className="text-sm font-bold text-brand-dark mt-0.5">{formatPrice(product.price)}</span>
                  </div>
                  <div className="flex flex-col items-center text-center p-3 rounded-xl bg-brand-light/50">
                    <Star size={20} className="text-warning mb-1.5" />
                    <span className="text-xs text-brand-gray">Rating</span>
                    <span className="text-sm font-bold text-brand-dark mt-0.5">5.0 / 5</span>
                  </div>
                  <div className="flex flex-col items-center text-center p-3 rounded-xl bg-brand-light/50">
                    <CheckCircle2 size={20} className="text-success mb-1.5" />
                    <span className="text-xs text-brand-gray">Status</span>
                    <span className="text-sm font-bold text-brand-dark mt-0.5">
                      {product.is_featured ? 'Featured' : 'Available'}
                    </span>
                  </div>
                  <div className="flex flex-col items-center text-center p-3 rounded-xl bg-brand-light/50">
                    <ShoppingBag size={20} className="text-success mb-1.5" />
                    <span className="text-xs text-brand-gray">Order</span>
                    <span className="text-sm font-bold text-brand-dark mt-0.5">Online</span>
                  </div>

                  {product.meta && Object.entries(product.meta).map(([key, value]) => (
                    <div key={key} className="flex flex-col items-center text-center p-3 rounded-xl bg-brand-light/50">
                      <ClipboardList size={20} className="text-brand-gray mb-1.5" />
                      <span className="text-xs text-brand-gray capitalize">{key}</span>
                      <span className="text-sm font-bold text-brand-dark mt-0.5">{String(value)}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* ── Description ── */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="bg-brand-white rounded-2xl border border-ui-border p-4 sm:p-8"
              >
                <h2 className="text-lg font-bold text-brand-dark mb-4">About This Item</h2>

                {product.short_description && (
                  <p className="text-base text-brand-dark leading-relaxed mb-4">
                    {product.short_description}
                  </p>
                )}

                {product.description && (
                  <div className="text-sm text-brand-gray leading-relaxed whitespace-pre-line">
                    {product.description}
                  </div>
                )}

                {!product.description && !product.short_description && (
                  <p className="text-sm text-brand-gray">
                    A delicious item from our menu, crafted with fresh, quality ingredients.
                  </p>
                )}
              </motion.div>

              {/* ── Similar Options with slider arrows ── */}
              <SimilarSlider items={similarProducts} />

              {/* ── Reviews Section ── */}
              <ReviewsSection productId={product.id} />
            </div>

            {/* ══ RIGHT COLUMN — Sticky sidebar ══ */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24 space-y-4">

                {/* Price card */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="bg-brand-white rounded-2xl border border-ui-border p-5 sm:p-6 shadow-sm"
                >
                  <p className="text-2xl sm:text-3xl font-extrabold text-brand-dark">
                    {formatPrice(product.price)}
                  </p>

                  <h1 className="text-base sm:text-lg font-bold text-brand-dark mt-2 leading-snug">
                    {product.title}
                  </h1>

                  <div className="flex items-center gap-2 mt-3">
                    <StarRating rating={5} size={16} />
                    <span className="text-sm text-brand-gray">Highly rated</span>
                  </div>

                  <button
                    onClick={() => setOrderOpen(true)}
                    className="w-full flex items-center justify-center gap-2 mt-5 py-3 sm:py-3.5 bg-success text-brand-white font-bold text-sm sm:text-base rounded-xl hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl"
                  >
                    <ClipboardList size={20} />
                    Place Order
                  </button>

                  <a
                    href={`tel:+${settings?.whatsapp_phone || '254700000000'}`}
                    className="w-full flex items-center justify-center gap-2 mt-3 py-2.5 sm:py-3 border-2 border-brand-red text-brand-red font-bold text-sm rounded-xl hover:bg-brand-red/5 transition-colors"
                  >
                    <Phone size={16} />
                    Make an Enquiry
                  </a>
                </motion.div>

                {/* Contact card */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="bg-brand-white rounded-2xl border border-ui-border p-5"
                >
                  <h3 className="text-sm font-bold text-brand-dark mb-3">Talk to us</h3>
                  <a
                    href={`tel:+${settings?.whatsapp_phone || '254700000000'}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-brand-light/50 hover:bg-brand-light transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-brand-red/10 flex items-center justify-center flex-shrink-0">
                      <Phone size={18} className="text-brand-red" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-brand-dark">+{settings?.whatsapp_phone || '254700000000'}</p>
                      <p className="text-xs text-brand-gray">Call us anytime</p>
                    </div>
                  </a>
                </motion.div>

                {/* Location card */}
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 }}
                  className="bg-brand-white rounded-2xl border border-ui-border p-5"
                >
                  <h3 className="text-sm font-bold text-brand-dark mb-3">Location</h3>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-brand-red/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MapPin size={18} className="text-brand-red" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-brand-dark">{settings?.restaurant_name || 'Smothers Restaurant'}</p>
                      <p className="text-xs text-brand-gray mt-0.5 flex items-center gap-1">
                        <MapPin size={10} className="text-brand-gray" />
                        {settings?.address || 'Nairobi, Kenya'}
                      </p>
                      <p className="text-xs text-brand-gray flex items-center gap-1">
                        <Clock size={10} className="text-brand-gray" />
                        {settings?.opening_hours || 'Mon – Sun, 10am – 10pm'}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

          </div>
        </div>
      </main>

      {/* ── Mobile sticky bottom CTA ── */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-brand-white border-t border-ui-border px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]">
        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-brand-gray truncate">{product.title}</p>
            <p className="text-lg font-extrabold text-brand-dark">{formatPrice(product.price)}</p>
          </div>
          <button
            onClick={() => setOrderOpen(true)}
            className="flex items-center gap-2 px-5 py-3 bg-success text-brand-white font-bold text-sm rounded-xl hover:bg-green-700 transition-colors shadow-md flex-shrink-0"
          >
            <ShoppingBag size={16} />
            Order
          </button>
        </div>
      </div>

      {/* Order modal */}
      <OrderModal
        product={product}
        isOpen={orderOpen}
        onClose={() => setOrderOpen(false)}
      />
    </>
  );
}

/* ── Reviews Section — horizontal slider + submission form ── */
function ReviewsSection({ productId }) {
  const { data: reviews, isLoading } = useProductReviews(productId);
  const submitMutation = useSubmitReview();
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', text: '', rating: 5 });
  const [error, setError] = useState('');
  const scrollRef = useRef(null);

  const scrollSlider = useCallback((dir) => {
    if (!scrollRef.current) return;
    const amount = 300;
    scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      await submitMutation.mutateAsync({
        name: form.name,
        text: form.text.slice(0, MAX_REVIEW_LENGTH),
        rating: form.rating,
        product_id: productId,
        email: form.email,
      });
      setSubmitted(true);
      setShowForm(false);
      setForm({ name: '', email: '', text: '', rating: 5 });
    } catch (err) {
      setError(err.message || 'Failed to submit review');
    }
  };

  const avgRating = reviews?.length
    ? (reviews.reduce((sum, r) => sum + (r.rating || 5), 0) / reviews.length).toFixed(1)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.35 }}
    >
      {/* Header with arrows */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquareHeart size={22} className="text-brand-red" />
          <h2 className="text-lg font-bold text-brand-dark">
            Reviews {reviews?.length ? `(${reviews.length})` : ''}
          </h2>
          {avgRating && (
            <div className="flex items-center gap-1 ml-2">
              <Star size={14} className="text-warning fill-warning" />
              <span className="text-sm font-bold text-brand-dark">{avgRating}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!showForm && !submitted && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-brand-white bg-brand-red hover:bg-red-700 rounded-lg transition-colors"
            >
              <Send size={12} /> Write a Review
            </button>
          )}
          {reviews?.length > 1 && (
            <>
              <button
                onClick={() => scrollSlider('left')}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-ui-border bg-brand-white hover:bg-brand-light transition-colors"
                aria-label="Scroll reviews left"
              >
                <ChevronLeft size={16} className="text-brand-dark" />
              </button>
              <button
                onClick={() => scrollSlider('right')}
                className="w-8 h-8 flex items-center justify-center rounded-full border border-ui-border bg-brand-white hover:bg-brand-light transition-colors"
                aria-label="Scroll reviews right"
              >
                <ChevronRight size={16} className="text-brand-dark" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Success message */}
      <AnimatePresence>
        {submitted && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="flex items-center gap-2 p-3 mb-4 bg-green-50 border border-green-200 text-success rounded-xl text-sm font-medium"
          >
            <CheckCircle2 size={16} />
            Thank you! Your review has been submitted and will appear once approved.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Review form */}
      <AnimatePresence>
        {showForm && (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
            className="mb-4 p-4 bg-brand-light rounded-xl space-y-3 overflow-hidden"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-brand-dark mb-1">Your Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-brand-dark mb-1">Email (optional)</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="john@example.com"
                  className="w-full px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-brand-dark mb-1">Rating</label>
              <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, rating: n }))}
                    className="p-0.5 hover:scale-110 transition-transform"
                  >
                    <Star
                      size={22}
                      className={clsx(
                        n <= form.rating ? 'text-warning fill-warning' : 'text-ui-border'
                      )}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-brand-dark mb-1">
                Your Review *
                <span className="text-brand-gray ml-1">{form.text.length}/{MAX_REVIEW_LENGTH}</span>
              </label>
              <textarea
                rows={3}
                value={form.text}
                onChange={(e) => setForm((f) => ({ ...f, text: e.target.value.slice(0, MAX_REVIEW_LENGTH) }))}
                placeholder="Share your experience..."
                className="w-full px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted resize-none"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-xs text-error font-medium">
                <AlertCircle size={14} /> {error}
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                type="submit"
                disabled={submitMutation.isPending}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-brand-white bg-brand-red hover:bg-red-700 rounded-xl transition-colors"
              >
                {submitMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Submit Review
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 text-sm font-bold text-brand-gray bg-brand-white hover:bg-gray-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>

      {/* Reviews — horizontal slider with arrow controls */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-brand-red" />
        </div>
      ) : !reviews?.length ? (
        <div className="bg-brand-white rounded-2xl border border-ui-border p-6">
          <p className="text-sm text-brand-gray text-center">
            No reviews yet. Be the first to share your experience!
          </p>
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide -mx-4 px-4"
        >
          {reviews.map((review, i) => {
            const initials = review.name
              ?.split(' ')
              .map((w) => w[0])
              .join('')
              .toUpperCase()
              .slice(0, 2) || '?';
            return (
              <motion.div
                key={review.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="snap-start flex-shrink-0 w-72 sm:w-80 bg-brand-white rounded-xl border border-ui-border p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-brand-red flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-brand-white">{initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-brand-dark truncate">{review.name}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <StarRating rating={review.rating || 5} size={12} />
                      <span className="text-[10px] text-brand-gray">
                        {new Date(review.created_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric',
                        })}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-brand-dark mt-3 leading-relaxed line-clamp-4">{review.text}</p>
              </motion.div>
            );
          })}
        </div>
      )}
    </motion.div>
  );
}
